import React, { useState, useEffect } from 'react';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Drawer, TextInputWithLabel, Button, Loader, Toast } from 'quantux-ui';
import { blue, gray, salmon } from '../../theme';
import { titleCase, BASE_URL } from '../../utils';

const AddUserDrawer = (props) => {
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isDisplayNameValid, setIsDisplayNameValid] = useState(false);
  const [isDisplayNamePristine, setIsDisplayNamePristine] = useState(true);
  const [userName, setUserName] = useState('');
  const [isUserNameValid, setIsUserNameValid] = useState(false);
  const [isUserNamePristine, setIsUserNamePristine] = useState(true);
  const [isUserNameDuplicate, setIsUserNameDuplicate] = useState(true);
  // -- Primary address
  const [primaryStreetAddress, setPrimaryStreetAddress] = useState('');
  const [primarySuite, setPrimarySuite] = useState('');
  const [primaryCity, setPrimaryCity] = useState('');
  const [primaryState, setPrimaryState] = useState('');
  const [primaryZip, setPrimaryZip] = useState('');
  // -- Mailing address
  const [mailingStreetAddress, setMailingStreetAddress] = useState('');
  const [mailingSuite, setMailingSuite] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingState, setMailingState] = useState('');
  const [mailingZip, setMailingZip] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailPristine, setIsEmailPristine] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(true);
  const [mobile, setMobile] = useState('');
  const [isMobilePristine, setIsMobilePristine] = useState(true);
  const [isMobileDuplicate, setIsMobileDuplicate] = useState(true);
  const [phone, setPhone] = useState('');
  const [fax, setFax] = useState('');
  const [website, setWebsite] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [providerFullName, setProviderFullName] = useState('');
  const [npi, setNpi] = useState('');
  const [canAddUser, setCanAddUser] = useState(false);
  const [providerCredentials, setProviderCredentials] = useState('');
  const [providerSpeciality, setProviderSpeciality] = useState('');
  const [providerTaxonomy, setProviderTaxonomy] = useState({});
  const [isAddingNewProvider, setIsAddingNewProvider] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const styles = StyleSheet.create({
    drawerContainer: {
      background: gray[500],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    }
  });

  const getUniqueUsername = (firstName, lastName) => {
    const url = new URL(`${BASE_URL}/users/getUsername`);
    const params = { firstName: firstName.charAt(0), lastName };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setUserName(data.userName);
        setIsUserNamePristine(false);
        setEmail(`${data.userName}@ggmail.com`);
        setIsEmailDuplicate(false);
        setIsEmailValid(true);
      })
      .catch(error => console.log('error', error));
  };

  useEffect(() => {
    const { number, basic, addresses, taxonomies } = props.data;

    if (basic) {
      setFirstName(titleCase(basic.first_name || ''));
      setMiddleName(titleCase(basic.middle_name || ''));
      setLastName(titleCase(basic.last_name || ''));
      getUniqueUsername(titleCase(basic.first_name || ''), titleCase(basic.last_name || ''));
      setNpi(number);
      setProviderFullName(`${basic.first_name} ${basic.last_name}`);
      setDisplayName(`${titleCase(basic.first_name)} ${titleCase(basic.last_name)}`);
      setIsDisplayNamePristine(false);
      setProviderCredentials(basic.credential || '');
    } else {
      const { firstName: fName = '', lastName: lName = '' } = props.data;
      setFirstName(titleCase(fName));
      setLastName(titleCase(lName));
      setDisplayName(`${titleCase(fName)} ${titleCase(lName)}`);
      setIsDisplayNamePristine(false);
      setMailingCity(props.data.city);
      setMailingState(props.data.state);
      setMailingZip(props.data.zip);
    }

    if (taxonomies && Array.isArray(taxonomies)) {
      const taxIdx = taxonomies.findIndex(el => el.primary);
      setProviderTaxonomy(taxonomies[taxIdx]);
      setProviderSpeciality(taxonomies[taxIdx].desc);
    }

    let tNumber = ''; // User telephone number (also used as mobile number)

    if (addresses && Array.isArray(addresses)) {
      addresses.forEach((add) => {
        // Telephone number from NPPES data
        if (add.telephone_number) {
          tNumber = add.telephone_number;
        }
        // Primary and Mailing addresses from NPPES data
        if (add.address_purpose === 'MAILING') {
          setMailingStreetAddress(add.address_1);
          setMailingCity(add.city);
          setMailingState(add.state);
          setMailingZip(add.postal_code);
        } else {
          setPrimaryStreetAddress(add.address_1);
          setPrimaryCity(add.city);
          setPrimaryState(add.state);
          setPrimaryZip(add.postal_code);
        }
      });

      setPhone(tNumber);
      setMobile(tNumber);
      setFax(tNumber);
    }
  }, [props.data]);

  useEffect(() => {
    setIsUserNameValid(userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0);
    if (userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0) {
      // console.log('Checking for duplicate username', userName);
      const url = new URL(`${BASE_URL}/users/exists/username/${userName}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          setIsUserNameDuplicate(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [userName]);

  useEffect(() => {
    setIsDisplayNameValid(displayName.length >= 2 && displayName.length <= 40);
  }, [displayName]);

  // Check for duplicate email
  useEffect(() => {
    if (isEmailValid) {
      const url = new URL(`${BASE_URL}/users/exists/email/${email}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          console.log(data);
          setIsEmailDuplicate(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [email]);

  // Check for duplicate mobile
  useEffect(() => {
    if (isMobilePhone(mobile)) {
      const formattedMobile = mobile.replaceAll('-', '');
      const url = new URL(`${BASE_URL}/users/exists/mobile/${formattedMobile}`);
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          console.log(data);
          setIsMobileDuplicate(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
    // const ddF = setTimeout(() => { searchUsersByLastName(); }, 300);
    // return () => clearTimeout(ddF);
  }, [mobile]);

  useEffect(() => {
    const _isValidEmail = isEmail(email);
    const _isValidMobile = isMobilePhone(mobile);
    const _isValidUserName =
      userName.trim().length >= 2 &&
      userName.trim().length <= 20 &&
      userName.trim().indexOf(' ') < 0;
    setCanAddUser(
      _isValidEmail && !isEmailDuplicate &&
      _isValidMobile && !isMobileDuplicate &&
      _isValidUserName && !isUserNameDuplicate
    );
    // debugLogToConsole(`Email: ${email} | valid ${_isValidEmail} | duplicate ${isEmailDuplicate}`);
    // debugLogToConsole(`Mobile: ${mobile} | valid ${_isValidMobile} | duplicate ${isMobileDuplicate}`);
    // debugLogToConsole(`UserName: ${userName} | valid ${_isValidUserName} | duplicate ${isUserNameDuplicate}`);
  }, [
    email,
    isEmailDuplicate,
    userName,
    isUserNameDuplicate,
    mobile,
    isMobileDuplicate
  ]);

  useEffect(() => {
    if (showToast) {
      setTimeout(() => {
        setShowToast(false);
      }, 5000);
    }
  }, [showToast]);

  const handleOnEditFirstName = (e) => {
    const { value } = e.target;
    setFirstName(value);
  };

  const handleOnEditMiddleName = (e) => {
    const { value } = e.target;
    setMiddleName(value);
  };

  const handleOnEditLastName = (e) => {
    const { value } = e.target;
    setLastName(value);
  };

  const handleOnEditDisplayName = (e) => {
    const { value } = e.target;
    setIsDisplayNamePristine(false);
    setIsDisplayNameValid(value.length >= 2 && value.length <= 40);
    setDisplayName(value);
  };

  const handleOnEditUserName = (e) => {
    const { value } = e.target;
    setIsUserNamePristine(false);
    setIsUserNameValid(value.length >= 2 && value.length <= 20);
    setUserName(value);
  };

  const handleOnEditStreetAddress = (e) => {
    const { value } = e.target;
    setMailingStreetAddress(value);
  };

  const handleOnEditSuite = (e) => {
    const { value } = e.target;
    setMailingSuite(value);
  };

  const handleOnEditCity = (e) => {
    const { value } = e.target;
    setMailingCity(value);
  };

  const handleOnEditState = (e) => {
    const { value } = e.target;
    setMailingState(value);
  };

  const handleOnEditZip = (e) => {
    const { value } = e.target;
    setMailingZip(value);
  };

  const handleOnEditPrimaryStreetAddress = (e) => {
    const { value } = e.target;
    setPrimaryStreetAddress(value);
  };

  const handleOnEditPrimarySuite = (e) => {
    const { value } = e.target;
    setPrimarySuite(value);
  };

  const handleOnEditPrimaryCity = (e) => {
    const { value } = e.target;
    setPrimaryCity(value);
  };

  const handleOnEditPrimaryState = (e) => {
    const { value } = e.target;
    setPrimaryState(value);
  };

  const handleOnEditPrimaryZip = (e) => {
    const { value } = e.target;
    setPrimaryZip(value);
  };

  const handleOnEditEmail = (e) => {
    const { value } = e.target;
    setIsEmailPristine(false);
    setIsEmailValid(isEmail(value));
    setEmail(value);
  };

  const handleOnEditMobile = (e) => {
    const { value } = e.target;
    console.log(`Mobile = ${value}`);
    setIsMobilePristine(false);
    setMobile(value);
  };

  const handleOnEditPhone = (e) => {
    const { value } = e.target;
    setPhone(value);
  };

  const handleOnEditFax = (e) => {
    const { value } = e.target;
    setFax(value);
  };

  const handleOnEditWebsite = (e) => {
    const { value } = e.target;
    setWebsite(value);
  };

  const handleOnChangeProfileDescription = (e) => {
    const { value } = e.target;
    setProfileDescription(value);
  };

  const handleOnClickSaveUserProfile = () => {
    setIsAddingNewProvider(true);
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('middleName', middleName);
    formData.append('displayName', displayName);
    formData.append('userName', userName);
    // Primary address
    formData.append('streetAddress', primaryStreetAddress);
    formData.append('suite', primarySuite);
    formData.append('city', primaryCity);
    formData.append('state', primaryState);
    formData.append('zip', primaryZip);
    // Mailing address
    formData.append('mailingStreetAddress', mailingStreetAddress);
    formData.append('mailingSuite', mailingSuite);
    formData.append('mailingCity', mailingCity);
    formData.append('mailingState', mailingState);
    formData.append('mailingZip', mailingZip);
    formData.append('email', email);
    formData.append('mobile', mobile);
    formData.append('phone', phone);
    formData.append('fax', fax);
    formData.append('website', website);
    formData.append('profileDescription', profileDescription);
    if (props.isProvider) {
      formData.append('npi', npi);
      formData.append('credentials', providerCredentials);
      formData.append('specialty', providerSpeciality);
      formData.append('taxonomy', JSON.stringify(providerTaxonomy));
    }

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/addUser2`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setIsAddingNewProvider(false);
          console.log(`New User ID = ${res.user._id}`);
          props.onAddProvider({
            fullName: `${titleCase(res.user.firstName)} ${titleCase(res.user.lastName)}`,
            npi: +npi,
            id: res.user._id,
            accessType: props.isProvider ? 'Provider' : 'Practice Manager',
            avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'
          });
          props.onClose();
        }
      })
      .catch(error => {
        console.log('error', error);
        setIsAddingNewProvider(false);
        setShowToast(true);
      });
  };

  return (
    <Drawer
      placement="right"
      visible={props.isVisible}
      toggleDrawer={() => {
        props.onClose();
      }}
      zIndex={props.zIndex || 2000}
      width={window.innerWidth <= 700 ? window.innerWidth : 358}
    >
      <div className={css(styles.drawerContainer)}>
        {/* Header */}
        <div className={css(styles.drawerHeader)}>
          <div className='fx fx__hv-center'>
            <i className='mr-2 fa fa-lg fa-user' />
            <div className='noselect'>Create User Profile</div>
          </div>
          <i
            className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
            onClick={() => {
              props.onClose();
            }}
          />
        </div>
        {showToast &&
          <Toast
            id={1}
            top="41"
            bottom={null}
            isCustomTemplate
            onDismiss={() => {
              setShowToast(false);
            }}
            showFooter={false}
          >
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '16px',
                padding: '0 8px'
              }}
            >
              <div>
                <i style={{ color: salmon[500], fontSize: '18px' }} className='fa fa-bell' />
                <span className='ml-2'>
                  Failed to create new provider
                </span>
              </div>
              <div>
                <i
                  style={{ color: salmon[500], fontSize: '18px' }}
                  className='fa fa-times'
                  onClick={() => {
                    setShowToast(false);
                  }}
                />
              </div>
            </div>
          </Toast>
        }
        {isAddingNewProvider &&
          <div style={{ height: '100%', background: blue[800] }}>
            <Loader iconType="circle" iconSize="3x" text={`Adding New ${props.isProvider ? 'Provider' : 'User'}`} />
          </div>
        }
        {/* Middle area */}
        {!isAddingNewProvider &&
          <div className={css(styles.drawerContentArea)}>
            {/* Last, Middle and First name */}
            <div
              className='ml-2 mt-1'
              style={{
                color: blue[400],
                fontSize: '14px',
                fontWeight: '700'
              }}
            >
              {props.isProvider
                ? <>
                  <div>{providerFullName}</div>
                  <div>{npi}</div>
                </>
                : <>
                  <div className='fx'>
                    {/* First Name */}
                    <div>
                      <TextInputWithLabel
                        value={firstName}
                        titleText="First Name"
                        titleFontSize={12}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={110}
                        onChange={handleOnEditFirstName}
                      />
                    </div>
                    {/* Middle Name */}
                    <div className='ml-2'>
                      <TextInputWithLabel
                        value={middleName}
                        titleText="Middle Name"
                        titleFontSize={12}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={108}
                        onChange={handleOnEditMiddleName}
                      />
                    </div>
                    {/* Last Name */}
                    <div className='ml-2'>
                      <TextInputWithLabel
                        value={lastName}
                        titleText="Last Name"
                        titleFontSize={12}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={108}
                        onChange={handleOnEditLastName}
                      />
                    </div>
                  </div>
                </>
              }
            </div>
            {/* DisplayName/UserName */}
            <div className='mt-2 ml-2'>
              <TextInputWithLabel
                value={displayName}
                titleText="Display Name (2 to 40 characters)*"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={341}
                onChange={handleOnEditDisplayName}
              />
              {/* Error message */}
              {!isDisplayNameValid && !isDisplayNamePristine &&
                <div className={css(styles.errMsg)}>
                  Invalid Display Name
                </div>
              }
            </div>
            {/* UserName */}
            <div
              className='ml-2'
              style={{
                marginTop: (!isDisplayNameValid && !isDisplayNamePristine) ? '0px' : '4px'
              }}
            >
              <TextInputWithLabel
                value={userName}
                titleText="Username (2 to 20 characters, no spaces)*"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor={blue[500]}
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={341}
                onChange={handleOnEditUserName}
              />
              {/* Error message */}
              {!isUserNameValid && !isUserNamePristine &&
                <div className={css(styles.errMsg)}>
                  Invalid User Name
                </div>
              }
              {isUserNameValid && !isUserNamePristine && isUserNameDuplicate &&
                <div className={css(styles.errMsg)}>
                  User Name Taken
                </div>
              }
            </div>
            {/* Primary Address */}
            <div className="mt-4 ml-2">
              {/* Heading */}
              <div className="fx fx__v-center">
                <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                <div className="ml-1">
                  Mailing Address
                </div>
              </div>
              {/* Form */}
              <div>
                {/* Address */}
                <div className="fx">
                  <div>
                    <TextInputWithLabel
                      value={primaryStreetAddress}
                      titleText="Street"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={284}
                      onChange={handleOnEditPrimaryStreetAddress}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={primarySuite}
                      titleText="Suite"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditPrimarySuite}
                    />
                  </div>
                </div>
                {/* City, State and Zip */}
                <div className="fx mt-2">
                  <div>
                    <TextInputWithLabel
                      value={primaryCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={230}
                      onChange={handleOnEditPrimaryCity}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={primaryState}
                      titleText="State"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditPrimaryState}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={primaryZip}
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditPrimaryZip}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Mailing Address */}
            <div className="mt-4 ml-2">
              {/* Heading */}
              <div className="fx fx__v-center">
                <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                <div className="ml-1">
                  Mailing Address
                </div>
              </div>
              {/* Form */}
              <div>
                {/* Address */}
                <div className="fx">
                  <div>
                    <TextInputWithLabel
                      value={mailingStreetAddress}
                      titleText="Street"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={284}
                      onChange={handleOnEditStreetAddress}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={mailingSuite}
                      titleText="Suite"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditSuite}
                    />
                  </div>
                </div>
                {/* City, State and Zip */}
                <div className="fx mt-2">
                  <div>
                    <TextInputWithLabel
                      value={mailingCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={230}
                      onChange={handleOnEditCity}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={mailingState}
                      titleText="State"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditState}
                    />
                  </div>
                  <div className="ml-2">
                    <TextInputWithLabel
                      value={mailingZip}
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={45}
                      onChange={handleOnEditZip}
                    />
                  </div>
                </div>
                {/* Email */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={email}
                    titleText="Email*"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-envelope'
                    onChange={handleOnEditEmail}
                  />
                  {!isEmailPristine && !isEmailValid
                    ? <div className={css(styles.errMsg)}>Invalid Email</div>
                    : null
                  }
                  {isEmailValid && !isEmailPristine && isEmailDuplicate &&
                    <div className={css(styles.errMsg)}>Email already in use</div>
                  }
                </div>
                {/* Mobile */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={mobile}
                    titleText="Mobile # *"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-mobile'
                    onChange={handleOnEditMobile}
                  />
                  {!isMobilePristine && !isMobilePhone(mobile)
                    ? <div className={css(styles.errMsg)}>Invalid Mobile #</div>
                    : null
                  }
                  {isMobilePhone(mobile) && !isMobilePristine && isMobileDuplicate &&
                    <div className={css(styles.errMsg)}>Mobile # already in use</div>
                  }
                </div>
                {/* Phone */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={phone}
                    titleText="Phone #"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-phone'
                    onChange={handleOnEditPhone}
                  />
                </div>
                {/* Fax */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={fax}
                    titleText="Fax #"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-fax'
                    onChange={handleOnEditFax}
                  />
                </div>
                {/* Website */}
                <div className="mt-2">
                  <TextInputWithLabel
                    value={website}
                    titleText="Website"
                    titleFontSize={12}
                    isCompactLayout={false}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={337}
                    showPreIcon
                    preIcon='fa fa-globe'
                    onChange={handleOnEditWebsite}
                  />
                </div>
              </div>
            </div>
            {/* Profile descriptiom */}
            <div className="mt-4 ml-2">
              <div>Profile Description</div>
              <div className={css(styles.profileTextAreaContainer)}>
                <textarea
                  className={css(styles.profileTextArea)}
                  placeholder="Write a description"
                  value={profileDescription}
                  onChange={handleOnChangeProfileDescription}
                />
              </div>
            </div>
          </div>
        }
        {/* Footer */}
        {!isAddingNewProvider &&
          <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickSaveUserProfile}
              isDisabled={!canAddUser}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Create User Profile
            </Button>
          </div>
        }
      </div>
    </Drawer>
  );
};

AddUserDrawer.defaultProps = {
  isProvider: true
};
export default AddUserDrawer;
